/**
 * Adapter Tela 4 Inbox do Time — SerializedPendencyRaw[] (GET /v1/pendencies)
 * → view-models. Agrupamento client-side (espelha o TeamInboxView do Claude Design;
 * o agregador /team só dá contagens, não os itens).
 *
 * Funções puras. Placeholders honestos (padrão Item 317):
 *   - customer: customerName do Engine (conversation→person) — placeholder só sem fonte.
 *   - opName: resolvido por `nameFor` (presence com nome real) — real.
 *   - conv: conversationId p/ navegação real ("Abrir conversa").
 */
import { relativeTime } from '../../../conversations/format';

import type {
  PendencyHeatmapCellRaw,
  PendencyTeamMetricsRaw,
  SerializedPendencyRaw,
} from '../../types';
import type {
  HeatmapRowVM,
  PendencyCardVM,
  PendencyGroupVM,
  TeamMetricsVM,
} from '../../view-models';

const PLACEHOLDER = '—';

/** 7 categorias canônicas ADR-109 + delivery (spec fila §D7, Rodada 2) — labels PT de operador (eixo 5; ícones/tones do design). */
export const PENDENCY_CATEGORIES: Record<string, { label: string; icon: string; tone: string }> = {
  customer_contact_required: { label: 'Contato com cliente', icon: '📞', tone: 'info' },
  document_collection: { label: 'Coleta de documentos', icon: '📄', tone: 'neutral' },
  external_action_required: { label: 'Ação externa', icon: '🔗', tone: 'neutral' },
  internal_review: { label: 'Revisão interna', icon: '🧐', tone: 'info' },
  technical_escalation: { label: 'Escalonamento técnico', icon: '🚨', tone: 'danger' },
  commitment_fulfillment_confirmation: {
    label: 'Confirmação de compromisso',
    icon: '💰',
    tone: 'warning',
  },
  ai_suggestion_review: { label: 'Revisão de sugestão IA', icon: '✨', tone: 'ai' },
  delivery: { label: 'Falha de entrega', icon: '📵', tone: 'danger' },
};

/** Status canônicos de Pendency (ADR-109) — label PT de operador (pill nunca mostra enum cru). */
export const PENDENCY_STATUS_LABELS: Record<string, string> = {
  open: 'Aberta',
  in_progress: 'Em progresso',
  resolved: 'Resolvida',
  cancelled: 'Cancelada',
  expired: 'Expirada',
  postponed: 'Adiada',
  archived: 'Arquivada',
};

/** Label PT de um status; humaniza token desconhecido (nunca enum cru). */
export function pendencyStatusLabel(status: string): string {
  return PENDENCY_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/** Label PT de uma categoria; humaniza token desconhecido (R2/R5 — nunca vazio). */
export function categoryLabel(cat: string | null): string {
  if (cat === null || cat.length === 0) return 'Sem categoria';
  return PENDENCY_CATEGORIES[cat]?.label ?? cat.replace(/_/g, ' ');
}

export function adaptPendencyCard(
  p: SerializedPendencyRaw,
  nameFor: (userId: string) => string,
  now: Date = new Date(),
): PendencyCardVM {
  let dueHours: number | null = null;
  let dueDays: number | null = null;
  let overdueLabel: string | null = null;

  if (p.deadline !== null) {
    const dl = new Date(p.deadline).getTime();
    if (p.overdue) {
      // Sem o prefixo "há " — o card prepende "vencida há " (mesmo padrão fmtAgo da Tela 7).
      overdueLabel = relativeTime(p.deadline, now).replace(/^há /, '');
    } else {
      const ms = dl - now.getTime();
      dueHours = Math.max(0, Math.round(ms / 3_600_000));
      dueDays = dueHours >= 24 ? Math.round(dueHours / 24) : null;
    }
  }

  return {
    id: p.id,
    cat: p.category,
    status: p.status,
    title: p.title,
    conv: p.conversationId,
    customer: p.customerName ?? PLACEHOLDER,
    opName: nameFor(p.ownerUserId),
    ownerUserId: p.ownerUserId,
    overdue: p.overdue,
    urgent: p.priority === 'urgent',
    overdueLabel,
    dueHours,
    dueDays,
    postponeCount: p.postponeCount,
    version: p.version,
  };
}

function buildGroup(key: string, items: PendencyCardVM[], icon?: string): PendencyGroupVM {
  const overdueCount = items.filter((i) => i.overdue).length;
  return {
    key,
    items,
    overdueCount,
    danger: overdueCount > 0,
    ...(icon !== undefined && { icon }),
  };
}

export function groupByOperator(cards: PendencyCardVM[]): PendencyGroupVM[] {
  const map = new Map<string, PendencyCardVM[]>();
  for (const c of cards) {
    const arr = map.get(c.opName) ?? [];
    arr.push(c);
    map.set(c.opName, arr);
  }
  return Array.from(map.entries())
    .map(([key, items]) => buildGroup(key, items))
    .sort((a, b) => {
      const byOverdue = b.overdueCount - a.overdueCount;
      return byOverdue !== 0 ? byOverdue : b.items.length - a.items.length;
    });
}

export function groupByCategory(cards: PendencyCardVM[]): PendencyGroupVM[] {
  const map = new Map<string, PendencyCardVM[]>();
  for (const c of cards) {
    const key = c.cat ?? '__null__';
    const arr = map.get(key) ?? [];
    arr.push(c);
    map.set(key, arr);
  }
  return Array.from(map.entries())
    .map(([catId, items]) =>
      buildGroup(
        catId === '__null__' ? 'Sem categoria' : categoryLabel(catId),
        items,
        catId === '__null__' ? undefined : PENDENCY_CATEGORIES[catId]?.icon,
      ),
    )
    .sort((a, b) => {
      const byOverdue = b.overdueCount - a.overdueCount;
      return byOverdue !== 0 ? byOverdue : b.items.length - a.items.length;
    });
}

export function groupByTimeline(cards: PendencyCardVM[]): PendencyGroupVM[] {
  const defs: { label: string; match: (c: PendencyCardVM) => boolean }[] = [
    { label: 'Vencidas', match: (c) => c.overdue },
    { label: 'Vence hoje', match: (c) => !c.overdue && c.dueHours !== null && c.dueHours <= 8 },
    {
      label: 'Vence em 24h',
      match: (c) => !c.overdue && c.dueHours !== null && c.dueHours > 8 && c.dueHours <= 24,
    },
    {
      label: 'Próximos 7 dias',
      match: (c) => !c.overdue && c.dueDays !== null && c.dueDays <= 7 && (c.dueHours ?? 0) > 24,
    },
    { label: 'Sem prazo', match: (c) => !c.overdue && c.dueHours === null && c.dueDays === null },
  ];
  const used = new Set<string>();
  const groups: PendencyGroupVM[] = [];
  for (const d of defs) {
    const items = cards.filter((c) => !used.has(c.id) && d.match(c));
    items.forEach((c) => used.add(c.id));
    if (items.length > 0) groups.push(buildGroup(d.label, items));
  }
  return groups;
}

export function adaptTeamMetrics(
  m: PendencyTeamMetricsRaw,
  nameFor: (userId: string) => string,
): TeamMetricsVM {
  return {
    oldestOverdue:
      m.oldestOverdueMinutes === null
        ? null
        : {
            hours: Math.round(m.oldestOverdueMinutes / 60),
            op:
              m.operatorWithMostOverdue === null
                ? PLACEHOLDER
                : nameFor(m.operatorWithMostOverdue.userId),
          },
    bottleneckOp:
      m.operatorWithMostOverdue === null
        ? null
        : {
            name: nameFor(m.operatorWithMostOverdue.userId),
            overdue: m.operatorWithMostOverdue.count,
          },
    bottleneckCat:
      m.categoryBottleneck === null
        ? null
        : {
            label: categoryLabel(m.categoryBottleneck.category),
            overdue: m.categoryBottleneck.count,
          },
  };
}

export interface TeamFilters {
  operator?: string;
  category?: string;
  status?: string;
  /** 'overdue' | 'today' | 'next_24h' | 'next_7d' | 'no_due' */
  dueStatus?: string;
}

function matchesDue(c: PendencyCardVM, dueStatus: string): boolean {
  if (dueStatus === 'overdue') return c.overdue;
  if (c.overdue) return false;
  if (dueStatus === 'today') return c.dueHours !== null && c.dueHours <= 8;
  if (dueStatus === 'next_24h') return c.dueHours !== null && c.dueHours > 8 && c.dueHours <= 24;
  if (dueStatus === 'next_7d')
    return c.dueDays !== null && c.dueDays <= 7 && (c.dueHours ?? 0) > 24;
  if (dueStatus === 'no_due') return c.dueHours === null && c.dueDays === null;
  return true;
}

export function filterCards(cards: PendencyCardVM[], f: TeamFilters): PendencyCardVM[] {
  return cards.filter((c) => {
    if (f.operator !== undefined && f.operator.length > 0 && c.opName !== f.operator) return false;
    if (f.category !== undefined && f.category.length > 0 && c.cat !== f.category) return false;
    if (f.status !== undefined && f.status.length > 0 && c.status !== f.status) return false;
    if (f.dueStatus !== undefined && f.dueStatus.length > 0 && !matchesDue(c, f.dueStatus))
      return false;
    return true;
  });
}

/** Coluna 0=Seg..6=Dom a partir do dayOfWeek do backend (0=Dom..6=Sáb). */
function colFor(dayOfWeek: number): number {
  return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
}

export function adaptHeatmap(
  cells: PendencyHeatmapCellRaw[],
  nameFor: (userId: string) => string,
): HeatmapRowVM[] {
  const map = new Map<string, number[]>();
  for (const cell of cells) {
    const days = map.get(cell.ownerUserId) ?? [0, 0, 0, 0, 0, 0, 0];
    days[colFor(cell.dayOfWeek)] = cell.hasOverdue ? -cell.count : cell.count;
    map.set(cell.ownerUserId, days);
  }
  return Array.from(map.entries()).map(([userId, days]) => ({ op: nameFor(userId), days }));
}

/** Converte dias relativos (mockup) → newDueAt ISO absoluto (contrato bulk-postpone). Âncora = now (D2). */
export function daysToNewDueAt(days: number, now: Date = new Date()): string {
  return new Date(now.getTime() + days * 86_400_000).toISOString();
}
